/**
 * RichTextEditor — lightweight contentEditable-based editor with a full
 * Outlook/Gmail-style toolbar. Zero third-party deps.
 *
 * Toolbar groups:
 *   1. Block format  → Paragraph, H1, H2, H3, Blockquote
 *   2. Font size     → 10, 12, 14, 16, 18, 24, 32 px (rendered via <font size=1..7>)
 *   3. Inline        → B · I · U · S (strikethrough)
 *   4. Color         → Text color, Highlight color
 *   5. Lists         → UL · OL · Indent · Outdent
 *   6. Alignment     → Left · Center · Right · Justify
 *   7. Insert        → Link · Image (URL) · Horizontal Rule
 *   8. Clear         → Remove formatting
 *
 * Imperative API (via ref): `insertText(text)`, `focus()`.
 */
import { useRef, useEffect, useImperativeHandle, forwardRef, useState, useCallback } from 'react';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  Strikethrough as StrikethroughIcon,
  List as ListIcon,
  ListOrdered as ListOrderedIcon,
  Link as LinkIcon,
  RemoveFormatting as RemoveFormattingIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Quote,
  Image as ImageIcon,
  Minus as MinusIcon,
  IndentIncrease,
  IndentDecrease,
  Palette,
  Highlighter,
} from 'lucide-react';

export function plainTextToHtml(text) {
  if (!text) return '';
  if (/<(?:p|br|div|ul|ol|blockquote)\b/i.test(text)) return text;
  const escape = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  const blocks = normalized.split(/\n\s*\n/);
  return blocks.map((block) => {
    const lines = block.split('\n');
    if (lines.every((l) => /^\s*(?:[-*•]|\u2022)\s+/.test(l))) {
      return '<ul>' + lines.map((l) => `<li>${escape(l.replace(/^\s*(?:[-*•]|\u2022)\s+/, ''))}</li>`).join('') + '</ul>';
    }
    if (lines.every((l) => /^\s*\d+[\.)]\s+/.test(l))) {
      return '<ol>' + lines.map((l) => `<li>${escape(l.replace(/^\s*\d+[\.)]\s+/, ''))}</li>`).join('') + '</ol>';
    }
    return `<p>${lines.map((l) => escape(l)).join('<br>')}</p>`;
  }).join('');
}

const ToolbarButton = ({ onClick, title, children, testId, active = false }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    title={title}
    data-testid={testId}
    className={
      'p-1.5 rounded transition-colors ' +
      (active
        ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200'
        : 'hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200')
    }
  >
    {children}
  </button>
);

const Divider = () => <span className="mx-1 h-5 w-px bg-slate-300 dark:bg-slate-600" />;

// Map desired CSS pixel sizes to execCommand fontSize values (1-7).
// execCommand only supports 1..7 as a legacy size index — we then rewrite the
// emitted <font size="N"> tags into <span style="font-size: Xpx"> for clean HTML.
const FONT_SIZE_OPTIONS = [
  { label: '10px', value: '1' },
  { label: '12px', value: '2' },
  { label: '14px', value: '3' },
  { label: '16px', value: '4' },
  { label: '18px', value: '5' },
  { label: '24px', value: '6' },
  { label: '32px', value: '7' },
];
const SIZE_TO_PX = { 1: '10px', 2: '12px', 3: '14px', 4: '16px', 5: '18px', 6: '24px', 7: '32px' };

const BLOCK_OPTIONS = [
  { label: 'Paragraph', value: 'p' },
  { label: 'Heading 1', value: 'h1' },
  { label: 'Heading 2', value: 'h2' },
  { label: 'Heading 3', value: 'h3' },
  { label: 'Quote', value: 'blockquote' },
];

const RichTextEditor = forwardRef(({ value, onChange, placeholder = 'Write your email...', minHeight = '200px' }, ref) => {
  const editorRef = useRef(null);
  const isFocused = useRef(false);
  const [isEmpty, setIsEmpty] = useState(!value);

  useEffect(() => {
    if (!editorRef.current) return;
    const currentHtml = editorRef.current.innerHTML;
    const nextHtml = value || '';
    if (!isFocused.current && currentHtml !== nextHtml) {
      editorRef.current.innerHTML = nextHtml;
      setIsEmpty(!editorRef.current.textContent?.trim());
    }
  }, [value]);

  const emitChange = useCallback(() => {
    if (!editorRef.current) return;
    setIsEmpty(!editorRef.current.textContent?.trim());
    onChange?.(editorRef.current.innerHTML);
  }, [onChange]);

  // Rewrite <font size="N"> → <span style="font-size: Xpx"> for cleaner HTML
  // (Gmail and Outlook both render size attributes, but spans survive pastes better.)
  const rewriteLegacyFontSizes = () => {
    if (!editorRef.current) return;
    const fonts = editorRef.current.querySelectorAll('font[size]');
    fonts.forEach((f) => {
      const px = SIZE_TO_PX[f.getAttribute('size')] || '14px';
      const span = document.createElement('span');
      span.style.fontSize = px;
      while (f.firstChild) span.appendChild(f.firstChild);
      // Preserve other font attrs if present
      const color = f.getAttribute('color');
      if (color) span.style.color = color;
      const face = f.getAttribute('face');
      if (face) span.style.fontFamily = face;
      f.parentNode.replaceChild(span, f);
    });
  };

  const exec = (cmd, arg = null) => {
    editorRef.current?.focus();
    try {
      document.execCommand(cmd, false, arg);
    } catch (_) { /* noop */ }
    if (cmd === 'fontSize' || cmd === 'foreColor' || cmd === 'hiliteColor' || cmd === 'backColor') {
      rewriteLegacyFontSizes();
    }
    emitChange();
  };

  useImperativeHandle(ref, () => ({
    insertText: (text) => {
      editorRef.current?.focus();
      try { document.execCommand('insertText', false, text); }
      catch (_) { if (editorRef.current) editorRef.current.innerText += text; }
      emitChange();
    },
    focus: () => editorRef.current?.focus(),
  }));

  const handleLink = () => {
    const url = window.prompt('Enter URL (https://…)');
    if (!url) return;
    exec('createLink', url);
  };

  const handleImage = () => {
    const url = window.prompt('Enter image URL (https://…)');
    if (!url) return;
    const safeUrl = url.replace(/"/g, '&quot;');
    exec('insertHTML', `<img src="${safeUrl}" style="max-width:100%;height:auto;" />`);
  };

  const handleHR = () => exec('insertHorizontalRule');

  const handleBlockFormat = (value) => {
    // execCommand formatBlock expects '<h1>', '<p>', '<blockquote>' etc.
    exec('formatBlock', `<${value}>`);
  };

  const handleFontSize = (value) => exec('fontSize', value);

  // Allow-list for the paste sanitizer below. Anything outside this list is
  // unwrapped (children kept) or stripped (for dangerous nodes).
  const PASTE_ALLOWED_TAGS = new Set([
    'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'P', 'BR', 'DIV', 'SPAN',
    'UL', 'OL', 'LI', 'A', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
    'BLOCKQUOTE', 'PRE', 'CODE', 'SUB', 'SUP',
    'TABLE', 'THEAD', 'TBODY', 'TR', 'TD', 'TH',
    'IMG', 'HR', 'FONT',
  ]);
  const PASTE_ALLOWED_ATTRS = new Set([
    'href', 'src', 'alt', 'title', 'target', 'rel',
    'colspan', 'rowspan', 'color', 'face',
  ]);
  const PASTE_ALLOWED_STYLES = new Set([
    'font-weight', 'font-style', 'text-decoration', 'text-decoration-line',
    'color', 'background-color', 'font-size', 'font-family',
    'text-align', 'line-height',
    'margin', 'margin-left', 'margin-right', 'margin-top', 'margin-bottom',
    'padding', 'padding-left',
    'list-style-type',
  ]);

  function sanitizePastedHtml(html) {
    if (!html) return '';
    // Strip Office-specific conditional comments early — they confuse DOMParser.
    const stripped = html
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/<\?xml[\s\S]*?\?>/g, '')
      .replace(/<o:p[^>]*>.*?<\/o:p>/gi, '')
      .replace(/<\/?o:p[^>]*>/gi, '');
    const doc = new DOMParser().parseFromString(
      `<!doctype html><body>${stripped}</body>`,
      'text/html'
    );
    // Hard-remove obviously dangerous / pointless nodes wholesale.
    doc.querySelectorAll(
      'script, style, meta, link, iframe, object, embed, form, input, button, noscript, base'
    ).forEach((n) => n.remove());

    const walk = (node) => {
      const kids = Array.from(node.childNodes);
      for (const child of kids) {
        if (child.nodeType === 8) {
          // comment
          child.parentNode?.removeChild(child);
          continue;
        }
        if (child.nodeType !== 1) continue;
        const tag = child.tagName.toUpperCase();
        if (!PASTE_ALLOWED_TAGS.has(tag)) {
          // Unwrap unknown tag — keep its children inline.
          const parent = child.parentNode;
          while (child.firstChild) parent.insertBefore(child.firstChild, child);
          parent.removeChild(child);
          continue;
        }
        // Filter attributes: drop everything except the allow-list, sanitize
        // style="...", strip on*= handlers and Office classes.
        const attrs = Array.from(child.attributes);
        for (const attr of attrs) {
          const name = attr.name.toLowerCase();
          if (name === 'style') {
            const safe = (attr.value || '')
              .split(';')
              .map((s) => s.trim())
              .filter(Boolean)
              .filter((s) => {
                const idx = s.indexOf(':');
                if (idx < 0) return false;
                const prop = s.slice(0, idx).trim().toLowerCase();
                return PASTE_ALLOWED_STYLES.has(prop);
              })
              .join('; ');
            if (safe) child.setAttribute('style', safe);
            else child.removeAttribute('style');
          } else if (name.startsWith('on') || !PASTE_ALLOWED_ATTRS.has(name)) {
            child.removeAttribute(attr.name);
          }
        }
        // Sanitize URLs: only http(s)/mailto/tel/anchor for <a>; only http(s)/data:image for <img>.
        if (tag === 'A') {
          const href = child.getAttribute('href') || '';
          if (!/^(https?:|mailto:|tel:|#)/i.test(href)) {
            child.removeAttribute('href');
          } else {
            child.setAttribute('rel', 'noopener noreferrer');
            child.setAttribute('target', '_blank');
          }
        }
        if (tag === 'IMG') {
          const src = child.getAttribute('src') || '';
          if (!/^(https?:|data:image\/)/i.test(src)) child.remove();
          else child.setAttribute('style', (child.getAttribute('style') || '') + ';max-width:100%;height:auto;');
        }
        walk(child);
      }
    };
    walk(doc.body);
    // Pasted bullets from Google Docs / Notion / Word often arrive as plain
    // paragraphs prefixed with a literal "•" or "1." marker. Convert runs of
    // such siblings into real <ul><li> / <ol><li> elements.
    promoteMarkerParagraphsToLists(doc.body);
    return doc.body.innerHTML;
  }

  // Marker detection for "fake" bullets pasted as text from external editors.
  // Returns { kind: 'ul'|'ol', text: cleanRemainder } or null.
  const BULLET_REGEX = /^\s*(?:[\u2022\u00b7\u25e6\u25aa\u25cf\u2023\u2043\u2219]|[-*])\s+/;
  const NUMBERED_REGEX = /^\s*(?:\(\d+\)|\d+[.)])\s+/;
  function detectMarker(rawText) {
    if (!rawText) return null;
    if (BULLET_REGEX.test(rawText)) {
      return { kind: 'ul', text: rawText.replace(BULLET_REGEX, '') };
    }
    if (NUMBERED_REGEX.test(rawText)) {
      return { kind: 'ol', text: rawText.replace(NUMBERED_REGEX, '') };
    }
    return null;
  }

  function promoteMarkerParagraphsToLists(root) {
    if (!root) return;
    // Block-level elements that can carry a "fake bullet" prefix.
    const PARA_TAGS = new Set(['P', 'DIV']);
    let node = root.firstChild;
    while (node) {
      const next = node.nextSibling;
      if (node.nodeType !== 1 || !PARA_TAGS.has(node.tagName)) {
        // Recurse into nested containers so lists pasted inside blockquotes /
        // divs are also fixed.
        if (node.nodeType === 1) promoteMarkerParagraphsToLists(node);
        node = next;
        continue;
      }
      const marker = detectMarker(node.textContent || '');
      if (!marker) {
        promoteMarkerParagraphsToLists(node);
        node = next;
        continue;
      }
      // Collect consecutive siblings carrying the SAME kind of marker.
      const run = [{ node, marker }];
      let cursor = node.nextSibling;
      while (cursor) {
        // Skip pure-whitespace text nodes between paragraphs.
        if (cursor.nodeType === 3 && !/\S/.test(cursor.textContent)) {
          cursor = cursor.nextSibling;
          continue;
        }
        if (cursor.nodeType !== 1 || !PARA_TAGS.has(cursor.tagName)) break;
        const m = detectMarker(cursor.textContent || '');
        if (!m || m.kind !== marker.kind) break;
        run.push({ node: cursor, marker: m });
        cursor = cursor.nextSibling;
      }
      if (run.length === 0) { node = next; continue; }
      // Build the <ul>/<ol>.
      const list = root.ownerDocument.createElement(marker.kind);
      run.forEach(({ node: p, marker: m }) => {
        const li = root.ownerDocument.createElement('li');
        // Preserve inline HTML (bold/italic/links) but strip the leading marker.
        // We clone the paragraph, then remove the marker by walking its first
        // text node.
        const clone = p.cloneNode(true);
        stripLeadingMarker(clone, m.kind === 'ul' ? BULLET_REGEX : NUMBERED_REGEX);
        while (clone.firstChild) li.appendChild(clone.firstChild);
        list.appendChild(li);
      });
      // Replace the first paragraph with the list, drop the others.
      const first = run[0].node;
      first.parentNode.insertBefore(list, first);
      run.forEach(({ node: p }) => p.parentNode && p.parentNode.removeChild(p));
      node = list.nextSibling;
    }
  }

  function stripLeadingMarker(elem, regex) {
    // Walk down to the first non-empty text node and remove the leading marker.
    let n = elem;
    while (n) {
      if (n.nodeType === 3) {
        const txt = n.textContent || '';
        if (/\S/.test(txt)) {
          n.textContent = txt.replace(regex, '');
          return;
        }
        n = n.nextSibling || (n.parentNode && n.parentNode.nextSibling);
        continue;
      }
      if (n.firstChild) { n = n.firstChild; continue; }
      n = n.nextSibling || (n.parentNode && n.parentNode.nextSibling);
    }
  }

  const handlePaste = (e) => {
    const cd = e.clipboardData;
    if (!cd) return;
    const html = cd.getData('text/html');
    const text = cd.getData('text/plain');
    if (html && html.trim()) {
      e.preventDefault();
      const cleaned = sanitizePastedHtml(html);
      document.execCommand('insertHTML', false, cleaned);
      emitChange();
      return;
    }
    if (text != null) {
      e.preventDefault();
      // Convert plain text to nice HTML (paragraphs, bullets, numbered lists).
      const htmlFromText = plainTextToHtml(text);
      document.execCommand('insertHTML', false, htmlFromText);
      emitChange();
    }
  };

  return (
    <div className="w-full border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 overflow-hidden">
      {/* Toolbar */}
      <div
        className="flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 flex-wrap"
        data-testid="rich-editor-toolbar"
      >
        {/* Block format */}
        <select
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => { handleBlockFormat(e.target.value); e.target.value = ''; }}
          defaultValue=""
          className="text-xs bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded px-1.5 py-1 text-slate-700 dark:text-slate-200 focus:outline-none"
          data-testid="rich-editor-block-format"
          title="Paragraph format"
        >
          <option value="" disabled>Format</option>
          {BLOCK_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        {/* Font size */}
        <select
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => { handleFontSize(e.target.value); e.target.value = ''; }}
          defaultValue=""
          className="text-xs bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded px-1.5 py-1 text-slate-700 dark:text-slate-200 focus:outline-none"
          data-testid="rich-editor-font-size"
          title="Font size"
        >
          <option value="" disabled>Size</option>
          {FONT_SIZE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <Divider />

        {/* Inline */}
        <ToolbarButton onClick={() => exec('bold')} title="Bold (Ctrl+B)" testId="rich-editor-bold"><BoldIcon className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('italic')} title="Italic (Ctrl+I)" testId="rich-editor-italic"><ItalicIcon className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('underline')} title="Underline (Ctrl+U)" testId="rich-editor-underline"><UnderlineIcon className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('strikeThrough')} title="Strikethrough" testId="rich-editor-strike"><StrikethroughIcon className="w-4 h-4" /></ToolbarButton>

        <Divider />

        {/* Text color */}
        <label className="relative inline-flex items-center cursor-pointer p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700" title="Text color">
          <Palette className="w-4 h-4 text-slate-700 dark:text-slate-200" />
          <input
            type="color"
            onInput={(e) => exec('foreColor', e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer"
            data-testid="rich-editor-color"
          />
        </label>

        {/* Highlight */}
        <label className="relative inline-flex items-center cursor-pointer p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700" title="Highlight color">
          <Highlighter className="w-4 h-4 text-slate-700 dark:text-slate-200" />
          <input
            type="color"
            defaultValue="#ffff00"
            onInput={(e) => exec('hiliteColor', e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer"
            data-testid="rich-editor-highlight"
          />
        </label>

        <Divider />

        {/* Lists & indent */}
        <ToolbarButton onClick={() => exec('insertUnorderedList')} title="Bulleted list" testId="rich-editor-ul"><ListIcon className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('insertOrderedList')} title="Numbered list" testId="rich-editor-ol"><ListOrderedIcon className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('outdent')} title="Decrease indent" testId="rich-editor-outdent"><IndentDecrease className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('indent')} title="Increase indent" testId="rich-editor-indent"><IndentIncrease className="w-4 h-4" /></ToolbarButton>

        <Divider />

        {/* Alignment */}
        <ToolbarButton onClick={() => exec('justifyLeft')} title="Align left" testId="rich-editor-align-left"><AlignLeft className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('justifyCenter')} title="Align center" testId="rich-editor-align-center"><AlignCenter className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('justifyRight')} title="Align right" testId="rich-editor-align-right"><AlignRight className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('justifyFull')} title="Justify" testId="rich-editor-align-justify"><AlignJustify className="w-4 h-4" /></ToolbarButton>

        <Divider />

        {/* Insert */}
        <ToolbarButton onClick={() => handleBlockFormat('blockquote')} title="Blockquote" testId="rich-editor-quote"><Quote className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={handleLink} title="Insert link" testId="rich-editor-link"><LinkIcon className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={handleImage} title="Insert image (URL)" testId="rich-editor-image"><ImageIcon className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={handleHR} title="Horizontal rule" testId="rich-editor-hr"><MinusIcon className="w-4 h-4" /></ToolbarButton>

        <Divider />

        <ToolbarButton onClick={() => exec('removeFormat')} title="Clear formatting" testId="rich-editor-clear"><RemoveFormattingIcon className="w-4 h-4" /></ToolbarButton>
      </div>

      {/* Editable area */}
      <div className="relative">
        {isEmpty && (
          <div className="absolute top-3 left-4 text-slate-400 pointer-events-none text-sm select-none">
            {placeholder}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={emitChange}
          onFocus={() => { isFocused.current = true; }}
          onBlur={() => { isFocused.current = false; emitChange(); }}
          onPaste={handlePaste}
          data-testid="rich-editor-content"
          className="px-4 py-3 text-sm text-slate-900 dark:text-white focus:outline-none
            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2
            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2
            [&_li]:my-0.5 [&_li>p]:m-0
            [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-3 [&_h1]:mb-2
            [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-2
            [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1
            [&_blockquote]:border-l-4 [&_blockquote]:border-slate-300 [&_blockquote]:dark:border-slate-600 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-slate-600 [&_blockquote]:dark:text-slate-400
            [&_a]:text-blue-600 [&_a]:dark:text-blue-400 [&_a]:underline
            [&_strong]:font-semibold
            [&_em]:italic
            [&_p]:my-1
            [&_pre]:bg-slate-100 [&_pre]:dark:bg-slate-800 [&_pre]:rounded [&_pre]:p-2 [&_pre]:my-2 [&_pre]:font-mono [&_pre]:text-xs
            [&_code]:bg-slate-100 [&_code]:dark:bg-slate-800 [&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs
            [&_hr]:my-3 [&_hr]:border-slate-200 [&_hr]:dark:border-slate-700"
          style={{ minHeight, lineHeight: '1.5' }}
        />
      </div>
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';
export default RichTextEditor;
